import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import * as api from "../../../shared/lib/api";
import ModalDelete from "../../../shared/components/modal-delete";
import {
	CertificateActionErrorProps,
	CertificateAuditAction,
	CertificateHistoryProps,
	CertificateStatus,
	ProviderCertificateAuditLogProps,
} from "../../../features/provider/types/provider";

const STATUS_LABEL: Record<CertificateStatus, string> = {
	pendente_ativacao: "Pendente de ativação",
	ativo: "Ativo",
	expirado: "Expirado",
};

const STATUS_COLOR: Record<CertificateStatus, "success" | "warning" | "error"> =
	{
		ativo: "success",
		pendente_ativacao: "warning",
		expirado: "error",
	};

const ACTION_LABEL: Record<CertificateAuditAction, string> = {
	created: "Criado",
	updated: "Atualizado (manual)",
	renewed: "Renovado",
	removed: "Removido",
};

type CertificateSectionProps = {
	providerId: string;
	certStatus: CertificateStatus | null;
	certGeneratedAt: string | null;
	certExpiresAt: string | null;
	onChanged: () => void;
};

export default function CertificateSection({
	providerId,
	certStatus,
	certGeneratedAt,
	certExpiresAt,
	onChanged,
}: CertificateSectionProps): JSX.Element {
	const [file, setFile] = useState<File | null>(null);
	const [password, setPassword] = useState<string>("");
	const [uploading, setUploading] = useState<boolean>(false);
	const [generating, setGenerating] = useState<boolean>(false);
	const [force, setForce] = useState<boolean>(false);
	const [removeOpen, setRemoveOpen] = useState<boolean>(false);
	const [removing, setRemoving] = useState<boolean>(false);
	const [history, setHistory] = useState<ProviderCertificateAuditLogProps[]>(
		[],
	);
	const [historyLoading, setHistoryLoading] = useState<boolean>(true);

	const getHistory = useCallback(async () => {
		setHistoryLoading(true);
		const response = (await api.get(
			`provider/${providerId}/certificate/history/`,
		)) as CertificateHistoryProps;
		setHistory(response.content ?? []);
		setHistoryLoading(false);
	}, [providerId]);

	useEffect(() => {
		getHistory().catch(console.error);
	}, [getHistory]);

	async function handleUpload() {
		if (!file || !password) {
			toast.error(
				"Selecione o arquivo .pfx e informe a senha do certificado.",
				{
					position: toast.POSITION.TOP_RIGHT,
				},
			);
			return;
		}

		setUploading(true);
		const formData = new FormData();
		formData.append("cert_pfx", file);
		formData.append("cert_password", password);

		const response = (await api.patch(
			`provider/${providerId}/certificate/`,
			formData,
		)) as { status: number };
		setUploading(false);

		if (response.status === 200) {
			toast.success("Certificado enviado com sucesso!", {
				position: toast.POSITION.TOP_RIGHT,
			});
			setFile(null);
			setPassword("");
			onChanged();
			getHistory();
		} else {
			toast.error(
				"Falha ao enviar o certificado. Confira o arquivo e a senha.",
				{
					position: toast.POSITION.TOP_RIGHT,
				},
			);
		}
	}

	async function handleGenerate() {
		setGenerating(true);
		const response = (await api.post(
			`provider/${providerId}/certificate/generate/`,
			{ force },
		)) as { status: number; data: CertificateActionErrorProps };
		setGenerating(false);

		if (response.status === 200) {
			toast.success("Certificado gerado/renovado com sucesso!", {
				position: toast.POSITION.TOP_RIGHT,
			});
			onChanged();
			getHistory();
		} else {
			toast.error(
				response?.data?.content?.detail ||
					"Falha ao gerar o certificado junto ao fornecedor.",
				{
					position: toast.POSITION.TOP_RIGHT,
				},
			);
		}
	}

	async function handleRemove() {
		setRemoving(true);
		const response = (await api.destroy(
			`provider/${providerId}/certificate/remove/`,
		)) as { status: number };
		setRemoving(false);
		setRemoveOpen(false);

		if (response.status === 204) {
			toast.success("Certificado removido com sucesso!", {
				position: toast.POSITION.TOP_RIGHT,
			});
			onChanged();
			getHistory();
		} else {
			toast.error("Falha ao remover o certificado.", {
				position: toast.POSITION.TOP_RIGHT,
			});
		}
	}

	return (
		<Box sx={{ width: "100%", mt: 4 }}>
			<ModalDelete
				open={removeOpen}
				textBody="Tem certeza que deseja remover o certificado digital desse fornecedor? Integrações que dependem de mTLS vão parar de funcionar até um novo certificado ser enviado ou gerado."
				handleModalClose={() => setRemoveOpen(false)}
				handleDelete={handleRemove}
				loading={removing}
			/>

			<Divider sx={{ mb: 2 }} />
			<Typography variant="h6" component="h3" gutterBottom>
				Certificado Digital (mTLS)
			</Typography>

			<Grid container spacing={2} alignItems="center">
				<Grid item>
					<Chip
						label={certStatus ? STATUS_LABEL[certStatus] : "Sem certificado"}
						color={certStatus ? STATUS_COLOR[certStatus] : "default"}
					/>
				</Grid>
				{certGeneratedAt && (
					<Grid item>
						<Typography variant="body2" color="text.secondary">
							Gerado em: {certGeneratedAt}
						</Typography>
					</Grid>
				)}
				{certExpiresAt && (
					<Grid item>
						<Typography variant="body2" color="text.secondary">
							Válido até: {certExpiresAt}
						</Typography>
					</Grid>
				)}
			</Grid>

			<Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
				<Grid item>
					<FormControlLabel
						control={
							<Checkbox
								checked={force}
								onChange={(e) => setForce(e.target.checked)}
							/>
						}
						label="Forçar geração mesmo com certificado ativo"
					/>
				</Grid>
				<Grid item>
					<Button
						variant="contained"
						color="primary"
						disabled={generating}
						onClick={handleGenerate}
					>
						{generating ? "Gerando..." : "Gerar / Renovar certificado"}
					</Button>
				</Grid>
				<Grid item>
					<Button
						variant="outlined"
						color="error"
						disabled={!certStatus}
						onClick={() => setRemoveOpen(true)}
					>
						Remover certificado
					</Button>
				</Grid>
			</Grid>

			<Typography variant="subtitle1" sx={{ mt: 3 }}>
				Enviar certificado manualmente
			</Typography>
			<Grid container spacing={2} alignItems="center" sx={{ mt: 0.5 }}>
				<Grid item>
					<Button variant="outlined" component="label">
						{file ? file.name : "Selecionar arquivo .pfx"}
						<input
							type="file"
							accept=".pfx"
							hidden
							onChange={(e) => setFile(e.target.files?.[0] ?? null)}
						/>
					</Button>
				</Grid>
				<Grid item>
					<TextField
						label="Senha do certificado"
						type="password"
						value={password}
						onChange={(e) => setPassword(e.target.value)}
					/>
				</Grid>
				<Grid item>
					<Button
						variant="contained"
						color="success"
						disabled={uploading}
						onClick={handleUpload}
					>
						{uploading ? "Enviando..." : "Enviar certificado"}
					</Button>
				</Grid>
			</Grid>

			<Typography variant="subtitle1" sx={{ mt: 3 }}>
				Histórico
			</Typography>
			<List dense>
				{historyLoading ? (
					<ListItem disablePadding>
						<ListItemText primary="Carregando histórico…" />
					</ListItem>
				) : history.length === 0 ? (
					<ListItem disablePadding>
						<ListItemText primary="Nenhuma ação registrada ainda." />
					</ListItem>
				) : (
					history.map((entry) => (
						<ListItem key={entry.id} disablePadding>
							<ListItemText
								primary={ACTION_LABEL[entry.action]}
								secondary={`${entry.changed_by ?? "Automático"} — ${entry.created_at}`}
							/>
						</ListItem>
					))
				)}
			</List>
		</Box>
	);
}
